/**
 * Preferências de interface por usuário E por tenant (spec 017, FR-006).
 *
 * Até 2026-09-28 só existia `localStorage` (a barra recolhida, por exemplo):
 * trocar de navegador ou de máquina voltava tudo ao padrão. Agora a escolha
 * mora em `tenant_members.ui_prefs` e segue a pessoa; o admin define o layout
 * padrão do tenant em `org_settings.default_layout`.
 *
 * Layout é um dos três eixos da D3: perfil do tenant (vocabulário), papel
 * (operador · gestor de cliente · cliente individual) e ESTA preferência. O
 * Completo de hoje fica congelado; o Simplificado é montado pela 018.
 */
import { z } from 'zod';

export const UI_LAYOUTS = ['classic', 'simplified'] as const;
export const UiLayoutSchema = z.enum(UI_LAYOUTS);
export type UiLayout = z.infer<typeof UiLayoutSchema>;

export const UI_LAYOUT_LABELS: Record<UiLayout, string> = {
  classic: 'Completa',
  simplified: 'Simplificada',
};

export const UI_DENSITIES = ['comfortable', 'compact'] as const;
export const UiDensitySchema = z.enum(UI_DENSITIES);
export type UiDensity = z.infer<typeof UiDensitySchema>;

export const UI_DENSITY_LABELS: Record<UiDensity, string> = {
  comfortable: 'Confortável',
  compact: 'Compacta',
};

/**
 * PUT /me/ui-prefs SUBSTITUI o objeto inteiro (mesma semântica do PUT
 * /me/notification-prefs) e recusa chave desconhecida com 422 — um typo no
 * front não pode virar uma preferência fantasma gravada. Chave ausente = o
 * padrão (layout do tenant, densidade confortável, barra aberta).
 */
export const UiPrefsSchema = z
  .object({
    layout: UiLayoutSchema,
    density: UiDensitySchema,
    sidebarCollapsed: z.boolean(),
  })
  .partial()
  .strict();
export type UiPrefs = z.infer<typeof UiPrefsSchema>;

/** Layout efetivo: a escolha da pessoa vence o padrão do tenant. */
export function resolveLayout(prefs: UiPrefs | null | undefined, tenantDefault: UiLayout): UiLayout {
  return prefs?.layout ?? tenantDefault;
}

/**
 * jsonb do banco → preferências válidas. Lixo (gravado fora do contrato) vira
 * "sem preferência" em vez de derrubar o /me; o chamador registra.
 */
export function parseStoredUiPrefs(raw: unknown): { prefs: UiPrefs; valid: boolean } {
  const parsed = UiPrefsSchema.safeParse(raw ?? {});
  return parsed.success ? { prefs: parsed.data, valid: true } : { prefs: {}, valid: false };
}
